import React, {CSSProperties, useEffect, useRef, useState} from 'react';
import {ImageValue} from "../../../../models/Template";
import Folder from "../../../../models/Folder";
import ImageSelectionGrid from "./ImageSelectionGrid";
import Asset from "../../../../models/Asset";
import {useTemplate} from "../../../../context/TemplateContext";
import {FormControl, Grid, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {CloseButton} from "../../components/CloseButton";
import {useTranslation} from "react-i18next";
import {styled} from "@mui/material/styles";
import {useApiService} from "../../../../context/ApiServiceContext";
import {toast} from "react-toastify";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import {BootstrapInput} from "../../../shared/form_controls/BootstrapInput";
import {useUsedAssets} from "./hooks/useUsedAssets";

export const CloseButtonContainer = styled(Grid)(({theme}) => ({
    display: 'flex',
    alignItems: 'center',
}));

interface ImageSelectionWrapperProps {
    onChange: (asset: Asset, key: string) => void;
    onClose: () => void;
    style?: CSSProperties;
    showUnusedOnly: boolean;
    setShowUnusedOnly: (value: boolean | ((prev: boolean) => boolean)) => void;
}

interface GridState {
    elementKey: string;
    value: ImageValue;
    folderId: string;
    data: any; // To hold API-fetched data or any internal state
}

const ImageSelectionWrapper: React.FC<ImageSelectionWrapperProps> = ({
                                                                         onChange,
                                                                         onClose,
                                                                         style,
                                                                         showUnusedOnly,
                                                                         setShowUnusedOnly,
                                                                     }) => {

    const {
        selectedImageData,
    } = useTemplate();
    const [gridHistory, setGridHistory] = useState<GridState[]>([]);
    const [currentState, setCurrentState] = useState<GridState>({
        elementKey: selectedImageData?.elementKey!,
        value: selectedImageData?.value!,
        folderId: selectedImageData?.folderId!,
        data: null, // Initial data state
    });
    const {t} = useTranslation();
    const apiService = useApiService();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [searchResults, setSearchResults] = useState<Asset[] | null>(null);
    const {usedAssetMap, hasSiblingInputs} = useUsedAssets(currentState.elementKey);

    function folderClick(folder: Folder) {
        // Save the current state to the history, including the data
        setGridHistory((prevHistory) => [...prevHistory, currentState]);
        setSearchTerm(''); // Clear search term when navigating to a new folder
        setSearchResults(null); // Clear search results

        // Update the current state for the new folder
        setCurrentState({
            elementKey: selectedImageData?.elementKey!,
            value: selectedImageData?.value!,
            folderId: folder.id,
            data: null, // Reset data for the new folder
        });
    }

    function handleBack() {
        setGridHistory((prevHistory) => {
            const updatedHistory = [...prevHistory];
            const lastState = updatedHistory.pop();

            if (lastState) {
                setSearchTerm(''); // Clear search term when going back
                setSearchResults(null); // Clear search results
                setCurrentState(lastState); // Restore the last saved state
            }

            return updatedHistory;
        });
    }

    function handleDataLoad(data: any) {
        // Update the current state's data once it is loaded
        setCurrentState((prevState) => ({
            ...prevState,
            data,
        }));
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setSearchResults(null);
            return;
        }

        setIsLoading(true);
        try {
            const response = await apiService.api.get(`/folder/${currentState.folderId}`, {
                params: {
                    assetName: searchTerm,
                }
            });

            // Filter only assets (not folders) from the response
            const assets = response.data.filter((item: any) => item.itemType === 'asset');
            setSearchResults(assets);
        } catch (error) {
            console.error('Error searching assets:', error);
            toast.error(t('error.assetSearch') || 'Failed to search assets');
            setSearchResults(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    // Clear search results when folderId changes
    useEffect(() => {
        setSearchResults(null);
        setSearchTerm('');
    }, [currentState.folderId]);

    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        const calculateHeight = () => {
            if (containerRef.current && headerRef.current) {
                // Get the container's height (either from style or parent constraints)
                const containerHeight = containerRef.current.clientHeight;
                const headerHeight = headerRef.current.clientHeight;

                // Calculate remaining height for content
                setContentHeight(containerHeight - headerHeight);
            }
        };

        // Initial calculation
        calculateHeight();

        // Set up resize observer for more reliable size detection
        const resizeObserver = new ResizeObserver(calculateHeight);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Grid
            ref={containerRef}
            container alignContent={"flex-start"} style={{
            padding: "10px 0",
            minHeight: "500px",
            maxHeight: "700px",
            height: "100%", // Fill available height
            overflow: "hidden"
        }}>

            <Grid item xs={12}
                  ref={headerRef}>
                <Grid container style={{
                    justifyContent: "space-between",
                    padding: "10px 0"
                }}>
                    <Grid item xs={8} style={{
                        fontSize: "18px"
                    }}>{selectedImageData?.value?.name}</Grid>
                    <CloseButtonContainer item xs={4} style={{
                        justifyContent: "flex-end"
                    }}>
                        <Typography marginRight={1}>{t("editor.imageSelector.close")}</Typography>
                        <CloseButton data-testid="image-gallery-close" onClick={onClose}>
                            <CloseIcon sx={{
                                fontSize: '20px',
                            }}/>
                        </CloseButton>
                    </CloseButtonContainer>
                </Grid>
                <Grid item xs={12}>
                    <FormControl fullWidth sx={{mb: 3}}>
                        <BootstrapInput
                            id={`image-search-input`}
                            variant="outlined"
                            fullWidth
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onKeyPress={handleKeyPress}
                            placeholder={t("editor.imageSelector.search")}
                            disabled={isLoading}
                            endAdornment={
                                <InputAdornment position="end" sx={{
                                    position: 'absolute',
                                    right: 12
                                }}>
                                    <IconButton
                                        edge="end"
                                        onClick={handleSearch}
                                        disabled={isLoading}
                                        sx={{ color: 'white' }}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </FormControl>
                </Grid>
                {hasSiblingInputs && (
                    <Grid item xs={12} sx={{mb: 1}}>
                        <Chip
                            label={showUnusedOnly
                                ? t("editor.imageSelector.showAll")
                                : t("editor.imageSelector.hideUsed")}
                            size="small"
                            variant={showUnusedOnly ? "filled" : "outlined"}
                            onClick={() => setShowUnusedOnly(prev => !prev)}
                            sx={{
                                color: '#fff',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&.MuiChip-filled': {
                                    backgroundColor: '#ed6c02',
                                },
                            }}
                        />
                    </Grid>
                )}
            </Grid>
            <Grid
                item
                xs={12}
            >
                <ImageSelectionGrid
                    elementKey={currentState.elementKey}
                    value={currentState.value}
                    onChange={onChange}
                    folderId={currentState.folderId}
                    onFolderClick={folderClick}
                    style={{
                        height: contentHeight ? `${contentHeight}px` : 'auto',
                        ...style
                    }}
                    showBackButton={gridHistory.length > 0}
                    handleBackClick={handleBack}
                    searchResults={searchResults}
                    isSearchActive={!!searchTerm.trim() && searchResults !== null}
                    handleClose={onClose}
                    usedAssetMap={usedAssetMap}
                    showUnusedOnly={showUnusedOnly}
                />
            </Grid>
        </Grid>
    );
};

export default ImageSelectionWrapper;